import React from "react";
import { useNavigate } from "react-router-dom";
import AddMovieHeader from "../components/AddMovieHeader";
import PageHeader from "../components/Header";
// import { useAddMovie } from "contexts/AdsMovieContext";

export default function Review({ onCancel, details }) {
  const navigate = useNavigate();

  // Using details (Media type) instead of state
  
  const handleCancel = () => {
    if (typeof onCancel === "function") onCancel();
    navigate(-1);
  };

  const InfoRow = ({ label, value }) => (
    <div className="mb-3">
      <strong className="block text-gray-800">{label}</strong>
      <span className="text-gray-700">{value || "—"}</span>
    </div>
  );

  const SectionBox = ({ title, onEdit, children }) => (
    <div className="bg-white rounded-lg border p-4 mb-6 shadow-sm">
      <div className="flex justify-between items-center border-b border-dashed pb-2 mb-3">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <button
          onClick={() => onEdit(details?.id)}
          className="px-3 py-1 text-sm border rounded-md bg-white shadow-sm hover:bg-gray-50"
        >
          Edit
        </button>
      </div>
      <div className="text-gray-700 text-sm">{children}</div>
    </div>
  );

  // Helper function to render arrays of objects
  const renderObjectArray = (array, nameKey, valueKey) => {
    if (!array || array.length === 0) return "—";
    return array.map(item => `${item[nameKey]}${valueKey ? `: ${item[valueKey]}` : ''}`).join(", ");
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <AddMovieHeader
        onCancel={handleCancel}
        showProgress={true}
        currentStep={6}
        continueDisabled={false}
        onContinue={() => alert("🎬 Movie Submitted!")}
        continueLabel="Submit Movie"
      />

      <main className="flex-1 flex justify-center px-4 py-8">
        <div className="w-full max-w-3xl">
          <PageHeader
            title="Review and Confirm"
            description="Take a moment to double-check your details before submitting."
          />

          <SectionBox
            title="General Information"
            onEdit={() => navigate(`/movie-box/add-movie/movie-details?id=${details?.id}`)}
          >
            <InfoRow label="ID" value={details?.id} />
            <InfoRow label="Title" value={details?.title} />
            <InfoRow label="Alternative Title" value={details?.alternative_title} />
            <InfoRow label="Tag Line" value={details?.tag_line} />
            <InfoRow label="Description" value={details?.description} />
            <div className="grid grid-cols-2 gap-4">
              <InfoRow label="Release Year" value={details?.release_year} />
              <InfoRow label="Status" value={details?.status} />
              <InfoRow label="Media Type" value={details?.media_type} />
            </div>
          </SectionBox>

          <SectionBox
            title="Country & Language"
            onEdit={() => navigate(`/movie-box/add-movie/movie-details?id=${details?.id}`)}
          >
            <div className="grid grid-cols-2 gap-4">
              <InfoRow 
                label="Country" 
                value={details?.country ? `${details.country.name} ${details.country.emoji}` : "—"} 
              />
              <InfoRow 
                label="Country Code" 
                value={details?.country?.code} 
              />
              <InfoRow 
                label="Language" 
                value={details?.language?.name} 
              />
              <InfoRow 
                label="Language Code" 
                value={details?.language?.code} 
              />
            </div>
          </SectionBox>

          <SectionBox
            title="Genres & Classifications"
            onEdit={() => navigate(`/movie-box/add-movie/movie-details?id=${details?.id}`)}
          >
            <InfoRow 
              label="Genres" 
              value={renderObjectArray(details?.genres, 'display_name')} 
            />
            <InfoRow 
              label="Classifications" 
              value={renderObjectArray(details?.classifications, 'display_name')} 
            />
          </SectionBox>

          <SectionBox
            title="Cast & Crew"
            onEdit={() => navigate(`/movie-box/add-movie/cast-crew?id=${details?.id}`)}
          >
            <InfoRow 
              label="Cast" 
              value={renderObjectArray(details?.cast, 'name', 'character')} 
            />
            <InfoRow 
              label="Crew" 
              value={renderObjectArray(details?.crew, 'name', 'role')} 
            />
          </SectionBox>

          <SectionBox
            title="Media Assets"
            onEdit={() => navigate(`/movie-box/add-movie/movie-artwork?id=${details?.id}`)}
          >
            <div className="grid grid-cols-2 gap-6">
              {details?.backdrop && (
                <div>
                  <strong className="block">Backdrop (Landscape)</strong>
                  <img
                    src={details.backdrop}
                    alt="Backdrop"
                    className="w-40 h-24 object-cover rounded mb-1"
                  />
                </div>
              )}
              {details?.poster && (
                <div>
                  <strong className="block">Poster (Portrait)</strong>
                  <img
                    src={details.poster}
                    alt="Poster"
                    className="w-24 h-40 object-cover rounded mb-1"
                  />
                </div>
              )}
              {!details?.backdrop && !details?.poster && (
                <p>No artwork available</p>
              )}
            </div>
          </SectionBox>

          <SectionBox
            title="Seasons"
            onEdit={() => navigate(`/movie-box/add-movie/seasons?id=${details?.id}`)}
          >
            {details?.seasons && details.seasons.length > 0 ? (
              <div>
                <InfoRow label="Number of Seasons" value={details.seasons.length} />
                {/* Add more detailed season information if needed */}
              </div>
            ) : (
              <p>No seasons available</p>
            )}
          </SectionBox>

          <SectionBox
            title="Trailers"
            onEdit={() => navigate(`/movie-box/add-movie/add-trailer?id=${details?.id}`)}
          >
            {details?.trailers && details.trailers.length > 0 ? (
              <div>
                <InfoRow label="Number of Trailers" value={details.trailers.length} />
                {/* Add more detailed trailer information if needed */}
              </div>
            ) : (
              <p>No trailers available</p>
            )}
          </SectionBox>
        </div>
      </main>
    </div>
  );
}
